/**
 * ArticleBarcodeModal (Jul-2026, User-Wunsch) — fehlende Hersteller-Barcodes an
 * Artikeln schnell nacherfassen.
 *  • „Am Handy scannen": zeigt einen QR-Code → Mitarbeiter öffnet die mobile
 *    Scan-Seite (ohne Login) und scannt die Barcodes. Live-Fortschritt.
 *  • „Hier erfassen": Liste der Artikel ohne Barcode, je Artikel Kamera-Scan
 *    (alle Formate via @zxing) oder manuelle Eingabe.
 *  • „Etiketten drucken": druckbares Barcode-Etiketten-PDF.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Smartphone, Printer, ScanLine, Check, Search, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import EanCameraScanner from "../../components/wms/EanCameraScanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ArticleBarcodeModal({ open, onClose }) {
    const [tab, setTab] = useState("mobile");
    const [total, setTotal] = useState(null);
    const [session, setSession] = useState(null);
    const [creating, setCreating] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [scanFor, setScanFor] = useState(null); // article id currently scanning
    const [manual, setManual] = useState({}); // { [id]: value }
    const pollRef = useRef(null);

    const loadCount = useCallback(async () => {
        try {
            const { data } = await api.get("/articles/without-barcode", { params: { limit: 1 } });
            setTotal(data.total_without_barcode);
        } catch { /* noop */ }
    }, []);

    const loadList = useCallback(async () => {
        setListLoading(true);
        try {
            const { data } = await api.get("/articles/without-barcode", { params: { q, limit: 50 } });
            setItems(data.items || []);
            setTotal(data.total_without_barcode);
        } catch (e) { toast.error("Liste konnte nicht geladen werden"); }
        finally { setListLoading(false); }
    }, [q]);

    useEffect(() => { if (open) { loadCount(); } }, [open, loadCount]);
    useEffect(() => { if (open && tab === "manual") { loadList(); } }, [open, tab, loadList]);

    // Live-Fortschritt der Handy-Session pollen
    useEffect(() => {
        if (!session?.token) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/articles/barcode-scan-sessions/${session.token}`);
                setSession((s) => (s ? { ...s, assigned_count: data.assigned_count } : s));
                setTotal(data.total_without_barcode);
            } catch { /* noop */ }
        }, 4000);
        return () => clearInterval(pollRef.current);
    }, [session?.token]);

    const startMobile = async () => {
        setCreating(true);
        try {
            const { data } = await api.post("/articles/barcode-scan-sessions");
            setSession({ ...data, assigned_count: 0 });
        } catch (e) { toast.error(e.response?.data?.detail || "Session konnte nicht erstellt werden"); }
        finally { setCreating(false); }
    };

    const saveEan = async (article, ean) => {
        const val = (ean || "").trim();
        if (!val) { toast.error("Kein Barcode erkannt"); return; }
        try {
            await api.post(`/articles/${article.id}/barcode`, { ean: val });
            toast.success(`„${article.name}" → ${val}`);
            setItems((prev) => prev.filter((a) => a.id !== article.id));
            setTotal((t) => (typeof t === "number" ? Math.max(0, t - 1) : t));
            setScanFor(null);
            setManual((m) => { const c = { ...m }; delete c[article.id]; return c; });
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "object" ? d.message : (d || "Speichern fehlgeschlagen"));
        }
    };

    const [withPrice, setWithPrice] = useState(false);
    const priceParam = withPrice ? "&price=true" : "";
    const printLabels = () => {
        window.open(`${BACKEND}/api/articles/labels.pdf?only_missing=true${priceParam}`, "_blank", "noopener");
    };

    const printQr = () => {
        const svg = document.querySelector('[data-testid="barcode-qr"] svg');
        if (!svg || !session) { toast.error("Kein QR-Code vorhanden"); return; }
        const w = window.open("", "_blank", "width=480,height=680");
        if (!w) { toast.error("Popup blockiert — bitte Pop-ups erlauben"); return; }
        w.document.write(`<html><head><title>Handy-Scan QR</title>
        <style>body{font-family:system-ui,Arial,sans-serif;text-align:center;padding:28px;color:#111827}
        .box{border:3px solid #111827;display:inline-block;padding:16px;border-radius:8px;margin:12px 0}
        h1{font-size:18px;margin:0 0 4px} h2{font-size:13px;color:#374151;font-weight:600;margin:0 0 16px}
        p{font-size:11px;color:#6b7280;word-break:break-all;max-width:360px;margin:8px auto}</style></head>
        <body><h1>Barcodes nacherfassen</h1><h2>Am Handy scannen</h2>
        <div class="box">${svg.outerHTML}</div>
        <p>${session.portal_url}</p>
        <p>QR-Code mit der Handy-Kamera scannen, um die mobile Erfassung zu öffnen (kein Login nötig).</p>
        <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
        </body></html>`);
        w.document.close();
    };

    const [generating, setGenerating] = useState(false);
    const generateInternal = async (andPrint = false) => {
        const verb = andPrint ? "erzeugen und die Etiketten drucken" : "erzeugen";
        if (!window.confirm(`Für alle Artikel ohne Barcode (${total ?? "?"}) einen internen Haus-Barcode (EAN-8) ${verb}? Diese Codes dienen dem internen Scannen/Etikettieren.`)) return;
        setGenerating(true);
        try {
            const { data } = await api.post("/articles/generate-internal-barcodes");
            toast.success(`${data.generated} interne Haus-Barcodes erzeugt.`);
            if (andPrint && data.batch_id) {
                window.open(`${BACKEND}/api/articles/labels.pdf?batch=${data.batch_id}${priceParam}`, "_blank", "noopener");
            }
            await loadCount();
            if (tab === "manual") await loadList();
        } catch (e) { toast.error(e.response?.data?.detail || "Erzeugung fehlgeschlagen"); }
        finally { setGenerating(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined} onPointerDownOutside={(e) => e.preventDefault()} className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="article-barcode-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ScanLine className="w-5 h-5 text-emerald-600" /> Barcodes nacherfassen
                        {typeof total === "number" && (
                            <span className="ml-auto mr-6 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5" data-testid="barcode-missing-count">
                                {total} ohne Barcode
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden mb-3">
                    <button type="button" onClick={() => setTab("mobile")} className={`h-9 px-4 text-xs font-bold uppercase tracking-wider ${tab === "mobile" ? "bg-[#111827] text-white" : "bg-white text-gray-600"}`} data-testid="barcode-tab-mobile">
                        <Smartphone className="w-3.5 h-3.5 inline mr-1" /> Am Handy scannen
                    </button>
                    <button type="button" onClick={() => setTab("manual")} className={`h-9 px-4 text-xs font-bold uppercase tracking-wider border-l border-gray-300 ${tab === "manual" ? "bg-[#111827] text-white" : "bg-white text-gray-600"}`} data-testid="barcode-tab-manual">
                        <ScanLine className="w-3.5 h-3.5 inline mr-1" /> Hier erfassen
                    </button>
                </div>

                {tab === "mobile" ? (
                    <div className="text-center space-y-3" data-testid="barcode-mobile-panel">
                        {!session ? (
                            <>
                                <p className="text-sm text-gray-600">QR-Code am Handy scannen, um die mobile Erfassung zu öffnen (kein Login nötig). Danach Artikel auswählen und Barcode scannen.</p>
                                <Button onClick={startMobile} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="barcode-start-mobile">
                                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Smartphone className="w-4 h-4 mr-1" /> QR-Code anzeigen</>}
                                </Button>
                            </>
                        ) : (
                            <div className="space-y-3">
                                <div className="inline-block bg-white p-3 border-2 border-[#111827] rounded-sm" data-testid="barcode-qr">
                                    <QRCodeSVG value={session.portal_url} size={200} level="M" />
                                </div>
                                <p className="text-xs text-gray-500 break-all">{session.portal_url}</p>
                                <div className="flex items-center justify-center gap-2">
                                    <Button variant="outline" size="sm" onClick={printQr} className="border-2 h-8 text-xs" data-testid="barcode-print-qr">
                                        <Printer className="w-3.5 h-3.5 mr-1" /> QR drucken
                                    </Button>
                                </div>
                                <div className="flex items-center justify-center gap-2 text-sm">
                                    <Check className="w-4 h-4 text-emerald-600" />
                                    <span data-testid="barcode-mobile-progress"><strong>{session.assigned_count || 0}</strong> Barcodes erfasst</span>
                                </div>
                                <p className="text-[11px] text-gray-400">Fortschritt aktualisiert sich automatisch. Gültig ca. 2 Stunden.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="barcode-manual-panel">
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadList()} placeholder="Suche Name, SKU, OEM-Nr." className="pl-8 border-2 border-emerald-500" data-testid="barcode-search" />
                            </div>
                            <Button variant="outline" onClick={loadList} className="border-2" data-testid="barcode-search-btn">
                                {listLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            </Button>
                        </div>
                        {items.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 py-8" data-testid="barcode-list-empty">
                                {listLoading ? "Lade…" : "Keine Artikel ohne Barcode gefunden."}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 border border-gray-200 rounded-sm max-h-[45vh] overflow-y-auto">
                                {items.map((a) => (
                                    <div key={a.id} className="p-2.5 flex items-center gap-2 flex-wrap" data-testid={`barcode-row-${a.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-sm truncate">{a.name}</div>
                                            <div className="text-[11px] text-gray-500">{[a.sku, a.manufacturer, a.category].filter(Boolean).join(" · ")}</div>
                                        </div>
                                        <Input
                                            value={manual[a.id] || ""}
                                            onChange={(e) => setManual((m) => ({ ...m, [a.id]: e.target.value }))}
                                            onKeyDown={(e) => e.key === "Enter" && saveEan(a, manual[a.id])}
                                            placeholder="Barcode…"
                                            className="h-8 w-[150px] font-mono-num"
                                            data-testid={`barcode-input-${a.id}`}
                                        />
                                        <Button size="sm" variant="outline" onClick={() => setScanFor(a.id)} className="h-8 border-2" data-testid={`barcode-scan-${a.id}`} title="Mit Kamera scannen">
                                            <ScanLine className="w-4 h-4" />
                                        </Button>
                                        <Button size="sm" onClick={() => saveEan(a, manual[a.id])} disabled={!manual[a.id]} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`barcode-save-${a.id}`}>
                                            <Check className="w-4 h-4" />
                                        </Button>
                                        {scanFor === a.id && (
                                            <EanCameraScanner
                                                onDetected={(code) => saveEan(a, code)}
                                                onClose={() => setScanFor(null)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="pt-1" />
                    </div>
                )}

                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 cursor-pointer select-none mr-1" data-testid="barcode-with-price-label">
                        <input type="checkbox" checked={withPrice} onChange={(e) => setWithPrice(e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid="barcode-with-price" />
                        mit Preis (Brutto)
                    </label>
                    <Button variant="outline" onClick={printLabels} className="border-2" data-testid="barcode-print-labels">
                        <Printer className="w-4 h-4 mr-1" /> Etiketten drucken (fehlende)
                    </Button>
                    <Button variant="outline" onClick={() => generateInternal(false)} disabled={generating} className="border-2 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="barcode-generate-internal" title="Für Artikel ohne Hersteller-Barcode einen internen Haus-Barcode (EAN-8) erzeugen">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1" /> Interne Haus-Barcodes erzeugen</>}
                    </Button>
                    <Button onClick={() => generateInternal(true)} disabled={generating} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="barcode-generate-print" title="Interne Haus-Barcodes erzeugen und direkt als Etiketten-Stapel drucken">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-1" /> Erzeugen &amp; drucken</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
